"use client"

import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from "react"

interface PerformanceMetrics {
  fps: number
  frameTime: number
  drawCalls: number
  triangles: number
  memoryUsage: number
}

interface PerformanceSettings {
  maxFPS: number
  enableLOD: boolean
  enableFrustumCulling: boolean
  enableOcclusion: boolean
  shadowQuality: "off" | "low" | "medium" | "high"
  postProcessingQuality: "off" | "low" | "medium" | "high"
  antiAliasing: boolean
  adaptiveQuality: boolean
}

interface PerformanceContextType {
  metrics: PerformanceMetrics
  settings: PerformanceSettings
  updateSettings: (newSettings: Partial<PerformanceSettings>) => void
  updateMetrics: (newMetrics: Partial<PerformanceMetrics>) => void
  throttledUpdate: (callback: () => void) => void
  shouldRender: (id: string) => boolean
  registerRenderObject: (id: string, priority: number) => void
  unregisterRenderObject: (id: string) => void
  isPerformanceModeEnabled: boolean
  enablePerformanceMode: () => void
  disablePerformanceMode: () => void
}

const PerformanceContext = createContext<PerformanceContextType | null>(null)

export function usePerformance() {
  const context = useContext(PerformanceContext)
  if (!context) {
    throw new Error("usePerformance must be used within a PerformanceProvider")
  }
  return context
}

interface PerformanceProviderProps {
  children: React.ReactNode
}

export function PerformanceProvider({ children }: PerformanceProviderProps) {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    frameTime: 16.67,
    drawCalls: 0,
    triangles: 0,
    memoryUsage: 0,
  })

  const [settings, setSettings] = useState<PerformanceSettings>({
    maxFPS: 60,
    enableLOD: true,
    enableFrustumCulling: true,
    enableOcclusion: false,
    shadowQuality: "medium",
    postProcessingQuality: "medium",
    antiAliasing: true,
    adaptiveQuality: true,
  })

  const [isPerformanceModeEnabled, setIsPerformanceModeEnabled] = useState(false)
  const renderObjects = useRef<Map<string, { priority: number; lastRender: number }>>(new Map())
  const frameCount = useRef(0)
  const lastThrottleTime = useRef(0)

  // FPS monitoring
  useEffect(() => {
    let frameId: number
    let lastTime = performance.now()
    let frameCounter = 0

    const measureFPS = (currentTime: number) => {
      frameCounter++
      frameCount.current++

      if (currentTime - lastTime >= 1000) {
        const fps = Math.round((frameCounter * 1000) / (currentTime - lastTime))
        setMetrics(prev => ({
          ...prev,
          fps,
          frameTime: 1000 / fps,
        }))

        // Auto-adjust quality based on performance
        if (settings.adaptiveQuality) {
          if (fps < 30 && !isPerformanceModeEnabled) {
            setIsPerformanceModeEnabled(true)
          } else if (fps > 50 && isPerformanceModeEnabled) {
            setIsPerformanceModeEnabled(false)
          }
        }

        frameCounter = 0
        lastTime = currentTime
      }

      frameId = requestAnimationFrame(measureFPS)
    }

    frameId = requestAnimationFrame(measureFPS)

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId)
      }
    }
  }, [settings.adaptiveQuality, isPerformanceModeEnabled])

  const updateSettings = useCallback((newSettings: Partial<PerformanceSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }))
  }, [])

  const updateMetrics = useCallback((newMetrics: Partial<PerformanceMetrics>) => {
    setMetrics(prev => ({ ...prev, ...newMetrics }))
  }, [])

  const throttledUpdate = useCallback((callback: () => void) => {
    const now = performance.now()
    const throttleMs = isPerformanceModeEnabled ? 100 : 50

    if (now - lastThrottleTime.current >= throttleMs) {
      callback()
      lastThrottleTime.current = now
    }
  }, [isPerformanceModeEnabled])

  const shouldRender = useCallback((id: string) => {
    const obj = renderObjects.current.get(id)
    if (!obj) return true

    const now = performance.now()
    const timeSinceLastRender = now - obj.lastRender

    // High priority objects render every frame
    if (obj.priority >= 3) return true

    // Medium priority objects render every other frame in performance mode
    if (obj.priority === 2) {
      const shouldRenderNow = !isPerformanceModeEnabled || frameCount.current % 2 === 0
      if (shouldRenderNow) {
        renderObjects.current.set(id, { ...obj, lastRender: now })
      }
      return shouldRenderNow
    }

    // Low priority objects render less frequently
    if (obj.priority === 1) {
      const interval = isPerformanceModeEnabled ? 200 : 100
      const shouldRenderNow = timeSinceLastRender >= interval
      if (shouldRenderNow) {
        renderObjects.current.set(id, { ...obj, lastRender: now })
      }
      return shouldRenderNow
    }

    // Very low priority objects
    const interval = isPerformanceModeEnabled ? 500 : 200
    const shouldRenderNow = timeSinceLastRender >= interval
    if (shouldRenderNow) {
      renderObjects.current.set(id, { ...obj, lastRender: now })
    }
    return shouldRenderNow
  }, [isPerformanceModeEnabled])

  const registerRenderObject = useCallback((id: string, priority: number) => {
    renderObjects.current.set(id, {
      priority,
      lastRender: performance.now(),
    })
  }, [])

  const unregisterRenderObject = useCallback((id: string) => {
    renderObjects.current.delete(id)
  }, [])

  const enablePerformanceMode = useCallback(() => {
    setIsPerformanceModeEnabled(true)
    setSettings(prev => ({
      ...prev,
      shadowQuality: "low",
      postProcessingQuality: "low",
      antiAliasing: false,
      maxFPS: 30,
    }))
  }, [])

  const disablePerformanceMode = useCallback(() => {
    setIsPerformanceModeEnabled(false)
    setSettings(prev => ({
      ...prev,
      shadowQuality: "medium",
      postProcessingQuality: "medium",
      antiAliasing: true,
      maxFPS: 60,
    }))
  }, [])

  const contextValue = useMemo(
    () => ({
      metrics,
      settings,
      updateSettings,
      updateMetrics,
      throttledUpdate,
      shouldRender,
      registerRenderObject,
      unregisterRenderObject,
      isPerformanceModeEnabled,
      enablePerformanceMode,
      disablePerformanceMode,
    }),
    [
      metrics,
      settings,
      updateSettings,
      updateMetrics,
      throttledUpdate,
      shouldRender,
      registerRenderObject,
      unregisterRenderObject,
      isPerformanceModeEnabled,
      enablePerformanceMode,
      disablePerformanceMode,
    ]
  )

  return <PerformanceContext.Provider value={contextValue}>{children}</PerformanceContext.Provider>
}